import React, { useState, useEffect, useRef } from 'react';
import { sendOtpEmail, sendOtpSms } from '../../services/otpService';
import './otpmodalscss/TwoFactorOtpModal.css';

const TwoFactorOtpModal = ({ 
  isOpen, 
  onClose, 
  targetValue,
  onSuccess,
  userEmail = '',
  userPhone = '',
  userFirstName = ''
}) => {
  const OTP_LENGTH = 6;
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [otpId, setOtpId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const [timeLeft, setTimeLeft] = useState(60); // 1 minute
  const [isExpired, setIsExpired] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('email');
  const [deliveryTarget, setDeliveryTarget] = useState('');
  const [hasSmsAvailable, setHasSmsAvailable] = useState(false);
  const [hasEmailAvailable, setHasEmailAvailable] = useState(false);
  const [hasSmsBeenSent, setHasSmsBeenSent] = useState(false);
  const [timerActive, setTimerActive] = useState(true);
  const inputsRef = useRef([]);

  // Initialize availability based on user contact info
  useEffect(() => {
    if (userEmail) {
      setHasEmailAvailable(true);
    }
    if (userPhone) {
      setHasSmsAvailable(true);
    }
  }, [userEmail, userPhone]);

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (isOpen) {
      inputsRef.current[0]?.focus();
    }
  }, [isOpen]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || !timerActive || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, timeLeft, timerActive]);

  // Send OTP when modal opens
  useEffect(() => {
    if (isOpen) {
      setHasSmsBeenSent(false);
      setTimerActive(true);
      handleSendOtp();
    }
  }, [isOpen]);

  const handleSendOtp = async (overrideMethod = null) => {
    const methodToUse =
      overrideMethod === 'email' || overrideMethod === 'sms'
        ? overrideMethod
        : verificationMethod;

    setIsLoading(true);
    setError('');

    try {
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      const requestId = `2fa_${Date.now()}`;
      const expiresAt = new Date(Date.now() + 5 * 60 * 1000).toISOString();

      setOtpId(requestId);

      if (methodToUse === 'email') {
        await sendOtpEmail({
          email: userEmail,
          otp,
          purpose: "two_factor_auth",
          requestId,
          expiresAt,
        });
        setDeliveryTarget(userEmail);
        setHasEmailAvailable(true);
      } else if (methodToUse === 'sms' && userPhone) {
        await sendOtpSms({
          phone: userPhone,
          otp,
          purpose: "two_factor_auth",
          requestId,
          expiresAt,
        });
        setDeliveryTarget(userPhone);
        setHasSmsAvailable(true);
      }

      // Store OTP in localStorage (like mobile uses AsyncStorage)
      localStorage.setItem(requestId, JSON.stringify({ otp, expiresAt }));
      
      setTimeLeft(60);
      setIsExpired(false);
    } catch (err) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTryAnotherWay = () => {
    const newMethod = verificationMethod === 'email' ? 'sms' : 'email';
    setVerificationMethod(newMethod);
    setHasSmsBeenSent(false);

    if (newMethod === 'sms' && userPhone) {
      setDeliveryTarget(userPhone);
      setTimerActive(false);
    } else if (newMethod === 'email' && userEmail) {
      setDeliveryTarget(userEmail);
      setTimerActive(false);
    }

    setOtp(Array(OTP_LENGTH).fill(""));
    setError('');
    setTimeLeft(60);
    setIsExpired(false);
  };

  const handleSendSms = async () => {
    if (isLoading) return;
    await handleSendOtp('sms');
    setHasSmsBeenSent(true);
    setTimerActive(true);
  };

  const handleSendEmail = async () => {
    if (isLoading) return;
    await handleSendOtp('email');
    setHasSmsBeenSent(false);
    setTimerActive(true);
  };

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData("text").trim().slice(0, OTP_LENGTH);
    if (/^\d+$/.test(pasteData)) {
      const pasteArray = pasteData.split("");
      const newOtp = Array(OTP_LENGTH).fill("");
      pasteArray.forEach((char, i) => {
        if (i < OTP_LENGTH) newOtp[i] = char;
      });

      setOtp(newOtp);
      setError("");
      const focusIndex = Math.min(pasteArray.length, OTP_LENGTH - 1);
      inputsRef.current[focusIndex]?.focus();
    }
    e.preventDefault();
  };

  const handleVerify = async () => {
    if (!otp.every(digit => digit !== "")) {
      setError("Please enter the full 6-digit code.");
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    if (isExpired) {
      setError("OTP expired. Please request a new code.");
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    setIsVerifying(true);
    try {
      if (!otpId) {
        throw new Error("No OTP request ID found");
      }

      const storedData = localStorage.getItem(otpId);
      if (!storedData) {
        throw new Error("OTP expired or invalid");
      }

      const { otp: storedOtp, expiresAt } = JSON.parse(storedData);
      
      if (new Date(expiresAt) < new Date()) {
        localStorage.removeItem(otpId);
        throw new Error("OTP expired");
      }

      if (otp.join("") !== storedOtp) {
        throw new Error("Invalid OTP");
      }

      localStorage.removeItem(otpId);
      
      if (onSuccess) {
        onSuccess(targetValue);
      }
      handleClose();
    } catch (err) {
      const errorMessage = err.message || 'Invalid OTP';
      setError(errorMessage);
      
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleClose = () => {
    setOtp(Array(OTP_LENGTH).fill(""));
    setError('');
    setOtpId('');
    setTimeLeft(60);
    setIsExpired(false);
    setVerificationMethod('email');
    setDeliveryTarget('');
    setHasSmsBeenSent(false);
    setTimerActive(true);
    onClose();
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Determine the display target with robust fallback:
  // 1. Use the actual deliveryTarget from the API response first
  // 2. Fall back to userPhone/userEmail props based on current verification method
  // This ensures the subtitle always shows a meaningful contact even if API has not returned yet
  const getDisplayTarget = () => {
    if (deliveryTarget && deliveryTarget.trim() !== '') {
      return deliveryTarget;
    }
    if (verificationMethod === 'sms' && userPhone) {
      return userPhone;
    }
    if (verificationMethod === 'email' && userEmail) {
      return userEmail;
    }
    return deliveryTarget || 'your contact';
  };

  if (!isOpen) return null;

  return (
    <div className="twofactor-otp-modal-overlay">
      <div className="twofactor-otp-modal-card">
        <h1 className="twofactor-otp-title">Verify Two-Factor Authentication</h1>
        <p className="twofactor-otp-subtitle">
          Enter 6-digit code we sent to: <strong>{getDisplayTarget()}</strong>
        </p>

        <div className={`twofactor-otp-inputs ${error ? "shake" : ""}`}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputsRef.current[index] = el)}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              className={`twofactor-otp-input ${error && !digit ? "error" : ""}`}
              disabled={isVerifying}
              autoFocus={index === 0}
            />
          ))}
        </div>

        {error && <div className="twofactor-otp-error-text">{error}</div>}

        {isExpired && (
          <div className="twofactor-otp-error-text">Code has expired. Please request a new one.</div>
        )}

        {/* Send SMS button (shown when in SMS mode but SMS not sent yet) */}
        {verificationMethod === 'sms' && !hasSmsBeenSent && (
          <button 
            className="twofactor-otp-resend-link" 
            onClick={handleSendSms}
            disabled={isLoading}
            style={{ 
              pointerEvents: isLoading ? "none" : "auto", 
              opacity: isLoading ? 0.5 : 1,
              marginBottom: "16px"
            }}
          >
            {isLoading ? "Sending..." : "Send SMS"}
          </button>
        )}

        {/* Send Email button (shown when switched from SMS to email) */}
        {hasSmsBeenSent && verificationMethod === 'email' && (
          <button 
            className="twofactor-otp-resend-link" 
            onClick={handleSendEmail}
            disabled={isLoading}
            style={{ 
              pointerEvents: isLoading ? "none" : "auto", 
              opacity: isLoading ? 0.5 : 1,
              marginBottom: "16px"
            }}
          >
            {isLoading ? "Sending..." : "Send Email"}
          </button>
        )}

        {/* Resend button (shown after SMS is sent or in email mode, but not when switched from SMS and timer is active) */}
        {timerActive && ((verificationMethod === 'email' && !hasSmsBeenSent) || hasSmsBeenSent) && (
          <p className="twofactor-otp-timer-section">
            {timeLeft > 0 ? (
              <span className={`twofactor-otp-timer ${isExpired ? "expired" : ""}`}>
                Resend in {formatTime(timeLeft)}
              </span>
            ) : (
              <span 
                className={`twofactor-otp-resend-link ${isLoading ? "disabled" : ""}`} 
                onClick={() => handleSendOtp()}
                style={{ 
                  pointerEvents: isLoading ? "none" : "auto", 
                  opacity: isLoading ? 0.5 : 1 
                }}
              >
                {isLoading ? "Sending..." : "Resend"}
              </span>
            )}
          </p>
        )}

        {((verificationMethod === 'email' && hasSmsAvailable) || 
          (verificationMethod === 'sms' && hasEmailAvailable)) && (
          <span 
            className="twofactor-otp-back-link" 
            onClick={handleTryAnotherWay}
            style={{ 
              textDecoration: "underline", 
              marginBottom: "16px", 
              display: "block",
              cursor: "pointer",
              color: "#1976d2"
            }}
          >
            Try another way
          </span>
        )}

        <button 
          className="twofactor-otp-verify-btn" 
          onClick={handleVerify} 
          disabled={isVerifying || !otp.every(d => d !== "") || isExpired}
        >
          {isVerifying ? "Verifying..." : "Verify"}
        </button>

        <button 
          className="twofactor-otp-cancel-btn" 
          onClick={handleClose}
          disabled={isVerifying}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default TwoFactorOtpModal;
